import React from 'react';

function MentalManagementGuide({ darkMode }) {
  const containerClass = darkMode 
    ? 'min-h-screen bg-gray-900 text-white' 
    : 'min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100';

  const cardClass = darkMode
    ? 'bg-gray-800 border-gray-700'
    : 'bg-white border-gray-200';

  const textClass = darkMode ? 'text-gray-300' : 'text-gray-600';

  return (
    <div className={containerClass}>
      <div className="container mx-auto px-4 py-8">
        {/* 헤더 */}
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <div className="text-6xl mb-4">💪</div>
            <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              취업 준비 멘탈관리법
            </h1>
            <p className={`text-lg ${textClass}`}>
              현실적이지만 포기하지 않는 마음가짐으로 🌟
            </p>
          </div>

          {/* 현실 인식 */}
          <div className={`${cardClass} rounded-2xl shadow-lg p-6 mb-6 border`}>
            <h2 className="text-2xl font-bold mb-4 flex items-center">
              <span className="text-2xl mr-3">🌊</span>
              현재 취업 시장 현실 직시하기
            </h2>
            <div className="space-y-4">
              <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
                <h3 className="font-semibold mb-2 text-blue-600">📊 냉정한 현실</h3>
                <ul className={`space-y-2 ${textClass}`}>
                  <li>• 2025년 5월 구직자 1인당 일자리 0.37개 (IMF 이후 최저)</li>
                  <li>• 중고신입(1-2년+ 경력자)도 신입 공고에 지원</li>
                  <li>• 인사직 등 일부 직무는 TO가 매우 제한적</li>
                  <li>• 평균 100회+ 지원이 일반적</li>
                </ul>
              </div>
              <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-green-50'}`}>
                <h3 className="font-semibold mb-2 text-green-600">✨ 하지만 기억하세요</h3>
                <ul className={`space-y-2 ${textClass}`}>
                  <li>• 어렵다 ≠ 불가능하다</li>
                  <li>• 매년 수많은 신입이 취업에 성공</li>
                  <li>• 꾸준히 도전하면 기회는 반드시 온다</li>
                  <li>• 지금의 어려움은 일시적</li>
                </ul>
              </div>
            </div>
          </div>

          {/* 심리적 대처법 */}
          <div className={`${cardClass} rounded-2xl shadow-lg p-6 mb-6 border`}>
            <h2 className="text-2xl font-bold mb-4 flex items-center">
              <span className="text-2xl mr-3">🧠</span>
              심리적 스트레스 이해하고 대처하기
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className={`p-4 rounded-xl ${darkMode ? 'bg-red-900/20' : 'bg-red-50'}`}>
                <h3 className="font-semibold mb-3 text-red-600">😰 스트레스 원인들</h3>
                <div className={`space-y-2 text-sm ${textClass}`}>
                  <div>🎯 <strong>불확실성:</strong> 언제 끝날지 모르는 막막함</div>
                  <div>💔 <strong>자존감 공격:</strong> 거절당할 때마다 자책</div>
                  <div>👥 <strong>사회적 압박:</strong> "언제 취업해?" 질문</div>
                  <div>💰 <strong>경제적 부담:</strong> 준비비용과 생활비</div>
                  <div>⏰ <strong>시간 압박:</strong> 나이 들어간다는 조급함</div>
                </div>
              </div>

              <div className={`p-4 rounded-xl ${darkMode ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
                <h3 className="font-semibold mb-3 text-blue-600">💡 대처 전략</h3>
                <div className={`space-y-2 text-sm ${textClass}`}>
                  <div>📅 <strong>작은 목표:</strong> 주간/월간 목표로 나누기</div>
                  <div>🎯 <strong>과정 중심:</strong> 결과보다 노력에 집중</div>
                  <div>🛡️ <strong>경계 설정:</strong> 타인 의견에 휘둘리지 않기</div>
                  <div>💝 <strong>자기 돌봄:</strong> 충분한 휴식과 취미 시간</div>
                  <div>📈 <strong>성장 마인드:</strong> 탈락도 배움의 기회</div>
                </div>
              </div>
            </div>
          </div>

          {/* 경제적 전략 */}
          <div className={`${cardClass} rounded-2xl shadow-lg p-6 mb-6 border`}>
            <h2 className="text-2xl font-bold mb-4 flex items-center">
              <span className="text-2xl mr-3">💰</span>
              경제적 부담 줄이는 현실적 전략
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-yellow-50'}`}>
                <h3 className="font-semibold mb-3 text-yellow-600">🎯 직무연관 경험 & 지원금 받기</h3>
                <div className={`space-y-3 ${textClass}`}>
                  <div>
                    <strong>🏢 아르바이트로 실무 경험:</strong>
                    <br />• 금융권: 은행 고객상담, 보험 텔레마케팅
                    <br />• 마케팅: SNS 관리, 마케팅 지원 알바
                    <br />• 사무직: 데이터 입력, 문서 작업 알바
                  </div>
                  <div>
                    <strong>💰 미래내일 일경험 (정부지원):</strong>
                    <br />• **기본형:** 8주간 월 15만원 (총 30만원)
                    <br />• **인턴형:** 12주간 최대 420만원까지!
                    <br />• 사전 직무교육 + 실제 기업 근무
                    <br />• 청년일경험 포털에서 신청
                  </div>
                  <div className="text-sm italic">
                    💡 <strong>검색 방법:</strong> 알바몬/알바천국 "지원직무+아르바이트", 
                    청년일경험 포털에서 희망 직무 선택!
                  </div>
                </div>
              </div>

              <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-green-50'}`}>
                <h3 className="font-semibold mb-3 text-green-600">💡 준비비용 줄이기</h3>
                <div className={`space-y-2 ${textClass}`}>
                  <div>📚 무료 강의: 유튜브, 정부 지원 프로그램</div>
                  <div>👔 면접복: 대여 서비스 또는 지인 빌리기</div>
                  <div>🎤 면접 연습: 무료 앱, 스터디 모임</div>
                  <div>📄 이력서: 무료 템플릿 활용</div>
                  <div>🚗 교통비: 화상면접 적극 활용</div>
                </div>
              </div>
            </div>
          </div>

          {/* 지지 시스템 */}
          <div className={`${cardClass} rounded-2xl shadow-lg p-6 mb-6 border`}>
            <h2 className="text-2xl font-bold mb-4 flex items-center">
              <span className="text-2xl mr-3">🤝</span>
              든든한 지지 시스템 만들기
            </h2>
            
            <div className="grid md:grid-cols-3 gap-4">
              <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-pink-50'}`}>
                <h3 className="font-semibold mb-3 text-pink-600">💕 개인적 지지망</h3>
                <div className={`space-y-2 text-sm ${textClass}`}>
                  <div>👨‍👩‍👧‍👦 이해해주는 가족, 친구</div>
                  <div>🎓 같은 상황의 동기들</div>
                  <div>👥 취준 스터디 모임</div>
                  <div>📱 온라인 커뮤니티</div>
                </div>
              </div>

              <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-blue-50'}`}>
                <h3 className="font-semibold mb-3 text-blue-600">🏛️ 지자체 서비스</h3>
                <div className={`space-y-2 text-sm ${textClass}`}>
                  <div>🏢 지자체 홈페이지</div>
                  <div>🌟 청년재단 프로그램</div>
                  <div>💼 경기도 잡아바 등</div>
                  <div>📞 취업 상담 서비스</div>
                </div>
              </div>

              <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-purple-50'}`}>
                <h3 className="font-semibold mb-3 text-purple-600">🧑‍⚕️ 전문 상담</h3>
                <div className={`space-y-2 text-sm ${textClass}`}>
                  <div>💚 심리상담사</div>
                  <div>💼 직업상담사</div>
                  <div>🆓 무료 상담 서비스</div>
                  <div>📞 전화 상담 핫라인</div>
                </div>
              </div>
            </div>
          </div>

          {/* 장기전 마인드셋 */}
          <div className={`${cardClass} rounded-2xl shadow-lg p-6 mb-6 border`}>
            <h2 className="text-2xl font-bold mb-4 flex items-center">
              <span className="text-2xl mr-3">🏃‍♂️</span>
              장기전 마인드셋으로 지치지 않기
            </h2>
            
            <div className="space-y-4">
              <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-orange-50'}`}>
                <h3 className="font-semibold mb-2 text-orange-600">🎯 양적 접근 전략</h3>
                <div className={`${textClass}`}>
                  <p className="mb-2">📈 <strong>100회+ 지원 각오하기</strong></p>
                  <ul className="space-y-1 text-sm ml-4">
                    <li>• 경쟁이 치열할수록 더 많은 기회 만들기</li>
                    <li>• 개인차는 있지만 양적 접근이 필요한 시대</li>
                    <li>• "많이 지원하면 하나는 걸린다" 마인드</li>
                  </ul>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-green-50'}`}>
                  <h3 className="font-semibold mb-2 text-green-600">✅ 할 일</h3>
                  <ul className={`space-y-1 text-sm ${textClass}`}>
                    <li>• 매일 소소한 성취 인정하기</li>
                    <li>• 규칙적인 생활 리듬 유지</li>
                    <li>• 취미활동으로 스트레스 해소</li>
                    <li>• 성장하는 모습 기록하기</li>
                    <li>• 적당한 휴식과 재충전</li>
                  </ul>
                </div>

                <div className={`p-4 rounded-xl ${darkMode ? 'bg-gray-700' : 'bg-red-50'}`}>
                  <h3 className="font-semibold mb-2 text-red-600">❌ 하지 말 것</h3>
                  <ul className={`space-y-1 text-sm ${textClass}`}>
                    <li>• 타인과 비교하며 자책하기</li>
                    <li>• 완벽주의에 사로잡히기</li>
                    <li>• 무리한 일정으로 번아웃</li>
                    <li>• 부정적 뉴스만 계속 보기</li>
                    <li>• 사회적 시선에만 신경쓰기</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* 희망 메시지 */}
          <div className={`${cardClass} rounded-2xl shadow-lg p-6 mb-6 border`}>
            <h2 className="text-2xl font-bold mb-4 flex items-center">
              <span className="text-2xl mr-3">🌅</span>
              성공 선배들의 현실적 조언
            </h2>
            
            <div className="space-y-4">
              <div className={`p-4 rounded-xl ${darkMode ? 'bg-indigo-900/20' : 'bg-indigo-50'} border-l-4 border-indigo-500`}>
                <p className={`italic mb-2 ${textClass}`}>
                  "저도 150번 넘게 지원하고 70번 넘게 떨어졌어요. 포기하고 싶었지만 끝까지 버텼더니 좋은 회사에 들어갔어요. 정말 끝까지 해보세요!"
                </p>
                <p className="text-sm text-indigo-600 font-semibold">- IT회사 신입 김○○님</p>
              </div>

              <div className={`p-4 rounded-xl ${darkMode ? 'bg-green-900/20' : 'bg-green-50'} border-l-4 border-green-500`}>
                <p className={`italic mb-2 ${textClass}`}>
                  "인사직이라서 포기할 뻔했는데, 작은 회사부터 도전해서 경험 쌓고 이직으로 대기업 왔어요. 우회로도 길이에요!"
                </p>
                <p className="text-sm text-green-600 font-semibold">- 대기업 인사팀 박○○님</p>
              </div>

              <div className={`p-4 rounded-xl ${darkMode ? 'bg-purple-900/20' : 'bg-purple-50'} border-l-4 border-purple-500`}>
                <p className={`italic mb-2 ${textClass}`}>
                  "멘탈이 정말 중요해요. 떨어져도 '내가 부족해서'가 아니라 '타이밍이 안 맞았구나' 생각하세요. 실력은 충분해요!"
                </p>
                <p className="text-sm text-purple-600 font-semibold">- 금융권 신입 이○○님</p>
              </div>
            </div>
          </div>

          {/* 응급처치 */}
          <div className={`${cardClass} rounded-2xl shadow-lg p-6 mb-8 border`}>
            <h2 className="text-2xl font-bold mb-4 flex items-center">
              <span className="text-2xl mr-3">🚨</span>
              멘탈 응급처치 키트
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className={`p-4 rounded-xl ${darkMode ? 'bg-yellow-900/20' : 'bg-yellow-50'}`}>
                <h3 className="font-semibold mb-3 text-yellow-600">😢 우울할 때</h3>
                <ul className={`space-y-2 text-sm ${textClass}`}>
                  <li>• 🚶‍♂️ 30분 산책하며 햇볕 쬐기</li>
                  <li>• 🎵 좋아하는 음악 듣기</li>
                  <li>• 📞 믿을 만한 사람과 통화</li>
                  <li>• 📝 감정 일기 쓰기</li>
                  <li>• 🛁 따뜻한 목욕 후 충분한 수면</li>
                </ul>
              </div>

              <div className={`p-4 rounded-xl ${darkMode ? 'bg-red-900/20' : 'bg-red-50'}`}>
                <h3 className="font-semibold mb-3 text-red-600">😤 분노할 때</h3>
                <ul className={`space-y-2 text-sm ${textClass}`}>
                  <li>• 🥊 운동으로 에너지 발산</li>
                  <li>• 🧘‍♂️ 심호흡과 명상</li>
                  <li>• 📝 분노 감정 적어보기</li>
                  <li>• 🎮 게임이나 취미 활동</li>
                  <li>• 💪 "이것도 지나간다" 반복하기</li>
                </ul>
              </div>
            </div>
            
            <div className={`mt-4 p-4 rounded-xl ${darkMode ? 'bg-red-900/30' : 'bg-red-100'} border border-red-300`}>
              <p className="text-red-600 font-semibold text-center">
                🚨 심각한 우울감이나 자해 충동이 느껴진다면 즉시 전문가 상담을 받으세요!
              </p>
              <p className={`text-center text-sm mt-2 ${textClass}`}>
                • 자살예방 상담전화: 109 (24시간) • 정신건강 위기상담: 1577-0199
              </p>
            </div>
          </div>

          {/* 버튼들 */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => window.history.back()}
              className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 text-center ${
                darkMode 
                  ? 'bg-blue-600 hover:bg-blue-700 text-white' 
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
              }`}
            >
              다른 가이드 보기
            </button>
            <button 
              onClick={() => window.location.href = '/'}
              className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 text-center border ${
                darkMode 
                  ? 'border-gray-600 text-gray-300 hover:bg-gray-800' 
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              홈으로 돌아가기
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MentalManagementGuide;